import { useEffect, useRef } from 'react';
import { CheckCircle2, AlertCircle, XCircle } from 'lucide-react';
import { Button } from '../../ui/button';

interface EligibilityStatus {
  canInvest: boolean;
  reasons: string[];
  nextSteps: string[];
}

interface EligibilityStepProps {
  eligibility: EligibilityStatus | null;
  onCheck: () => EligibilityStatus;
  onContinue: () => void;
  onGoToSettings: () => void;
}

export function EligibilityStep({
  eligibility,
  onCheck,
  onContinue,
  onGoToSettings,
}: EligibilityStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
    // Run check on mount if not already done
    if (!eligibility) onCheck();
  }, []);

  const isEligible = eligibility?.canInvest ?? false;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          Account Verification
        </h1>
        <p className="text-lg text-muted-foreground">
          Let's make sure your account is ready to invest
        </p>
      </div>

      {eligibility && !isEligible ? (
        <div className="space-y-6">
          <div className="rounded-xl border-2 border-border bg-card p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-medium mb-2">Setup Required</h3>
                <p className="text-muted-foreground mb-4">
                  Please complete the following before investing:
                </p>
                <ul className="space-y-2">
                  {eligibility.reasons.map((reason, index) => (
                    <li key={index} className="flex items-center gap-2 text-muted-foreground">
                      <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-xl border-2 border-border bg-card p-6">
            <h3 className="text-lg font-medium mb-3">Next Steps</h3>
            <ol className="space-y-3">
              {eligibility.nextSteps.map((step, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-secondary text-foreground rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <Button className="w-full h-14 text-lg" onClick={onGoToSettings}>
            Complete Setup
          </Button>
        </div>
      ) : isEligible ? (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-12 h-12 text-foreground" />
          </div>
          <h3 className="text-xl font-medium mb-2">Account Verified</h3>
          <p className="text-muted-foreground mb-8">Your account is ready to invest in The Hive Fund</p>

          <Button className="w-full h-14 text-lg" onClick={onContinue}>
            Continue
          </Button>
        </div>
      ) : null}
    </div>
  );
}
